import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  Package,
  AlertCircle,
  BarChart2,
  ClipboardCheck,
  PackagePlus,
  MessageSquare,
  HelpCircle,
  Home,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import abcLogo from '../../assets/abc-logo.webp';

/**
 * Navigation items configuration.
 */
const navItems = [
  {
    section: 'Main',
    items: [
      { path: '/', label: 'Home', icon: Home },
    ],
  },
  {
    section: 'Data Entries',
    items: [
      { path: '/products', label: 'Products Manager', icon: Package },
      { path: '/issues', label: 'Issues Resolution', icon: AlertCircle },
      { path: '/clarifications', label: 'Clarifications', icon: MessageSquare },
      { path: '/shopwise-production', label: 'Shopwise Production', icon: BarChart2 },
    ],
  },
  {
    section: 'QC',
    items: [
      { path: '/qc', label: 'QC Tool', icon: ClipboardCheck },
    ],
  },
  {
    section: 'Management',
    items: [
      { path: '/new-products-requests', label: 'New Products Requests', icon: PackagePlus },
      { path: '/products-tickets', label: 'Products Tickets', icon: MessageSquare },
      { path: '/clarifications-management', label: 'Clarifications Mgmt', icon: HelpCircle },
    ],
  },
];

/**
 * Sidebar navigation component.
 *
 * @param {Object} props
 * @param {boolean} [props.collapsed=false] - Whether sidebar is collapsed
 * @param {function} [props.onToggle] - Called when toggle button clicked
 * @param {string} [props.className] - Additional classes
 */
export function Sidebar({
  collapsed = false,
  onToggle,
  className,
}) {
  const location = useLocation();

  return (
    <aside
      aria-label="Main navigation"
      className={cn(
        'flex flex-col h-full bg-white border-r border-neutral-200 transition-all duration-200',
        collapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Logo area */}
      <div className={cn(
        'flex items-center h-20 px-4 border-b border-neutral-200',
        collapsed ? 'justify-center' : 'justify-between'
      )}>
        {!collapsed && (
          <Link to="/" title="Go to Home">
            <img
              src={abcLogo}
              alt="ABC Consulting"
              className="h-12 hover:opacity-80 transition-opacity"
            />
          </Link>
        )}
        {onToggle && (
          <button
            onClick={onToggle}
            className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navItems.map((section) => (
          <div key={section.section} className="mb-4">
            {!collapsed && (
              <div className="px-4 mb-2">
                <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                  {section.section}
                </span>
              </div>
            )}
            <ul className="space-y-1 px-2">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900',
                        collapsed && 'justify-center'
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}
